/**
 * Os cinco tipos de conexão (03/11/2026, decisão do Gabriel).
 *
 * O cruzamento procura, para cada participante, pessoas destes tipos. Cada pessoa
 * tem dois lados:
 *  - **procura**: os tipos de pessoa que ela procura;
 *  - **atende**: os tipos em que ela serve para quem procura.
 *
 * A conecta com B quando um tipo que A procura está no "atende" de B. Uma
 * fundadora procura `socio_investidor`, e um investidor atende `socio_investidor`.
 * Um executivo em transição procura `colocacao_no_mercado` e atende
 * `busca_de_executivos`; a empresa que contrata faz o inverso.
 *
 * ⚠️ **"Comprador e Fornecedor" é UM tipo para quem lê e DUAS chaves por dentro.**
 * Com uma chave só, dois fornecedores procurando compradores casariam entre si.
 * `fornecedor` é "procura um fornecedor" (e quem é fornecedor o atende);
 * `comprador` é "procura um comprador" (e quem compra o atende).
 *
 * Módulo próprio, como `recomendacoes.ts`, para a SPA importar só isto.
 */

export const CHAVES_DOS_TIPOS_DE_CONEXAO = [
  'socio_investidor',
  'socio_operador',
  'colocacao_no_mercado',
  'busca_de_executivos',
  'fornecedor',
  'comprador',
] as const;

export type TipoDeConexao = (typeof CHAVES_DOS_TIPOS_DE_CONEXAO)[number];

export interface DescricaoDoTipo {
  /** O nome do tipo, como aparece na tela. */
  rotulo: string;
  /** Detalhe do tipo 5, que tem dois sentidos. */
  sentido?: string;
  /** O que é PROCURAR este tipo, para a IA e para quem lê. */
  procura: string;
  /** O que é ATENDER quem procura este tipo. */
  atende: string;
}

export const TIPOS_DE_CONEXAO: Record<TipoDeConexao, DescricaoDoTipo> = {
  socio_investidor: {
    rotulo: 'Sócio Investidor',
    procura: 'procura um sócio que entre com capital no negócio dela',
    atende: 'é investidor e quer entrar como sócio num negócio',
  },
  socio_operador: {
    rotulo: 'Sócio Operador',
    procura: 'procura um sócio que toque a operação do negócio com ela',
    atende: 'quer entrar como sócio operando um negócio',
  },
  colocacao_no_mercado: {
    rotulo: 'Colocação no Mercado',
    procura: 'procura uma nova posição profissional (recolocação)',
    atende: 'tem uma posição para oferecer a um profissional',
  },
  busca_de_executivos: {
    rotulo: 'Busca de Executivos',
    procura: 'procura um executivo de alto nível (CEO, COO, CFO, CTO) para o negócio dela',
    atende: 'é executivo de alto nível e está aberto a uma nova posição',
  },
  fornecedor: {
    rotulo: 'Comprador e Fornecedor',
    sentido: 'procura fornecedor',
    procura: 'procura um fornecedor para o que ela compra',
    atende: 'é fornecedor e vende o que ela compra',
  },
  comprador: {
    rotulo: 'Comprador e Fornecedor',
    sentido: 'procura comprador',
    procura: 'procura compradores para o que ela vende',
    atende: 'compra o que ela vende',
  },
};

/** "Sócio Investidor", ou "Comprador e Fornecedor · procura fornecedor". */
export function rotuloDoTipo(tipo: TipoDeConexao): string {
  const descricao = TIPOS_DE_CONEXAO[tipo];
  return descricao.sentido ? `${descricao.rotulo} · ${descricao.sentido}` : descricao.rotulo;
}

/** A chave, se o texto for uma. Para ler JSON livre do perfil sem confiar nele. */
export function ehTipoDeConexao(valor: unknown): valor is TipoDeConexao {
  return typeof valor === 'string' && (CHAVES_DOS_TIPOS_DE_CONEXAO as readonly string[]).includes(valor);
}

/**
 * Os tipos que uma pessoa procura e a outra atende: é o que faz o tipo casar.
 * Num sentido só — para os dois, chame com os lados trocados.
 */
export function tiposAtendidos(
  procura: readonly TipoDeConexao[],
  atende: readonly TipoDeConexao[],
): TipoDeConexao[] {
  const doOutro = new Set(atende);
  return [...new Set(procura)].filter((tipo) => doOutro.has(tipo));
}

/**
 * Os tipos em palavras simples, para a pergunta da entrevista. Os dois sentidos
 * do tipo 5 viram um item só, como a pessoa diria.
 */
export const TIPOS_EM_PALAVRAS_SIMPLES =
  'um sócio investidor, um sócio operador, uma nova posição no mercado, ' +
  'um executivo para o seu negócio, ou compradores e fornecedores';
